import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Movie, Showtime } from '../../models';
import { MovieService } from '../../services/movie.service';
import { CinemaShowtimeGroup, CinemaShowtimeGroupComponent } from '../../components/cinema-showtime-group.component';
import { DateChipOption, DateChipRowComponent } from '../../components/date-chip-row.component';
import { LocationFilterService } from '../../services/location-filter.service';

@Component({
  selector: 'app-movie-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, CinemaShowtimeGroupComponent, DateChipRowComponent],
  template: `
    <section class="movie-detail-page">
      <div class="container">
        <div *ngIf="loadingMovie" class="compact-loading"></div>
        <div *ngIf="error" class="error-state">{{ error }}</div>

        <ng-container *ngIf="movie">
          <div class="movie-summary">
            <div class="poster-thumb">
              <img *ngIf="movie.posterUrl" [src]="movie.posterUrl" [alt]="movie.title + ' poster'" />
              <span *ngIf="!movie.posterUrl">{{ initials(movie.title) }}</span>
            </div>
            <div>
              <h1>{{ movie.title }}</h1>
              <div class="summary-meta">
                <span>{{ showtimes.length }} showtime{{ showtimes.length === 1 ? '' : 's' }}</span>
                <span>{{ grouped().length }} cinema{{ grouped().length === 1 ? '' : 's' }}</span>
                <span>{{ movie.runtimeMinutes ? movie.runtimeMinutes + ' min' : 'Runtime TBC' }}</span>
                <span *ngIf="movie.language">{{ movie.language }}</span>
              </div>
            </div>
          </div>

          <div class="detail-search">
            <div class="glass search-panel" role="search">
              <input class="input search-input" [(ngModel)]="query" placeholder="Search cinema or town" (ngModelChange)="scheduleQueryUpdate()" (keyup.enter)="updateQueryParams()" />
              <div class="filter-line">
                <input class="input" [(ngModel)]="date" type="date" aria-label="Date" (ngModelChange)="updateQueryParams()" />
                <select [(ngModel)]="county" aria-label="County" (ngModelChange)="updateQueryParams()">
                  <option value="">All counties</option>
                  <option *ngFor="let county of counties">{{ county }}</option>
                </select>
                <button class="clear-btn" type="button" (click)="clearFilters()">Clear</button>
              </div>
            </div>

            <app-date-chip-row [options]="availableDates()" [value]="date" (selected)="selectDate($event)" />
          </div>

          <section class="showtime-results">
            <div class="compact-head">
              <h2>Showtimes</h2>
              <span>{{ grouped().length }} cinema{{ grouped().length === 1 ? '' : 's' }} · {{ filteredShowtimes().length }} time{{ filteredShowtimes().length === 1 ? '' : 's' }}</span>
            </div>

            <div *ngIf="loadingShowtimes" class="compact-loading"></div>
            <div *ngIf="!loadingShowtimes && !grouped().length" class="empty-state compact-empty">No showtimes found for this movie with the selected filters.</div>

            <div class="cinema-groups" *ngIf="!loadingShowtimes && grouped().length">
              <app-cinema-showtime-group *ngFor="let group of grouped()" [group]="group" />
            </div>
          </section>
        </ng-container>
      </div>
    </section>
  `,
  styles: [
    `
      .movie-detail-page {
        padding: 14px 0 72px;
      }

      .movie-summary {
        display: grid;
        grid-template-columns: 70px minmax(0, 1fr);
        gap: 12px;
        align-items: center;
        margin-bottom: 10px;
      }

      .poster-thumb {
        width: 70px;
        aspect-ratio: 2 / 3;
        border: 1px solid var(--poster-border);
        border-radius: 13px;
        overflow: hidden;
        display: grid;
        place-items: center;
        color: var(--text);
        background: linear-gradient(135deg, var(--blue), var(--violet));
        font-size: 18px;
        font-weight: 950;
      }

      .poster-thumb img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      h1 {
        margin: 0 0 6px;
        color: var(--text);
        font-size: clamp(24px, 4vw, 38px);
        line-height: 1.05;
        overflow-wrap: anywhere;
      }

      .summary-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
      }

      .summary-meta span {
        min-height: 22px;
        border-radius: 999px;
        padding: 4px 7px;
        color: var(--faint);
        background: var(--control-bg);
        font-size: 11px;
        font-weight: 850;
      }

      .detail-search {
        position: sticky;
        top: 68px;
        z-index: 18;
        display: grid;
        gap: 7px;
        padding: 9px 0 8px;
        margin: 0 0 2px;
        background: linear-gradient(180deg, var(--bg) 0%, rgba(7, 9, 20, 0.94) 78%, transparent 100%);
        backdrop-filter: blur(14px);
      }

      .search-panel {
        display: grid;
        grid-template-columns: minmax(250px, 1fr) minmax(420px, 0.85fr);
        gap: 9px;
        padding: 9px;
        border-radius: 17px;
        box-shadow: 0 12px 34px rgba(0, 0, 0, 0.22);
      }

      .search-input {
        font-weight: 850;
      }

      .filter-line {
        display: grid;
        grid-template-columns: minmax(132px, 1fr) minmax(136px, 1fr) auto;
        gap: 8px;
      }

      .search-panel select {
        min-height: 44px;
        border: 1px solid var(--line);
        border-radius: 999px;
        padding: 0 14px;
        color: var(--text);
        background: var(--field-bg);
      }

      .search-panel .input {
        min-height: 44px;
      }

      .clear-btn {
        min-height: 44px;
        border: 1px solid var(--line);
        border-radius: 999px;
        padding: 0 13px;
        color: var(--text);
        background: var(--control-bg);
        font-weight: 900;
        cursor: pointer;
      }

      .showtime-results {
        padding-top: 4px;
      }

      .compact-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin: 7px 0 9px;
      }

      .compact-head h2 {
        margin: 0;
        font-size: 18px;
        line-height: 1.1;
      }

      .compact-head span {
        color: var(--muted);
        font-size: 12px;
        font-weight: 900;
        white-space: nowrap;
      }

      .cinema-groups {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
      }

      .compact-loading {
        min-height: 120px;
        border-radius: 16px;
        background: var(--skeleton);
        background-size: 220% 100%;
        animation: shimmer 1.25s linear infinite;
      }

      .compact-empty {
        padding: 18px 14px;
        border-radius: 16px;
      }

      @media (max-width: 1024px) {
        .detail-search {
          top: 64px;
        }

        .search-panel {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 720px) {
        .movie-detail-page {
          padding-top: 10px;
        }

        .movie-summary {
          grid-template-columns: 52px minmax(0, 1fr);
          gap: 10px;
          margin-bottom: 4px;
        }

        .poster-thumb {
          width: 52px;
          border-radius: 11px;
          font-size: 14px;
        }

        h1 {
          font-size: 21px;
        }

        .summary-meta span:nth-child(n+4) {
          display: none;
        }

        .detail-search {
          padding: 7px 0;
          gap: 6px;
        }

        .search-panel {
          padding: 8px;
          gap: 8px;
          border-radius: 16px;
        }

        .filter-line {
          grid-template-columns: 1fr 1fr auto;
        }

        .clear-btn {
          min-width: 58px;
          padding: 0 10px;
        }

        .compact-head {
          margin: 6px 0 8px;
        }

        .compact-head h2 {
          font-size: 17px;
        }

        .compact-head span {
          font-size: 11px;
        }

        .cinema-groups {
          grid-template-columns: 1fr;
          gap: 7px;
        }
      }

      @media (max-width: 430px) {
        .filter-line {
          grid-template-columns: 1fr 1fr;
        }

        .clear-btn {
          display: none;
        }
      }

      @media (max-width: 380px) {
        .filter-line {
          grid-template-columns: 1fr;
        }
      }
    `,
  ],
})
export class MovieDetailComponent implements OnInit {
  movie?: Movie;
  showtimes: Showtime[] = [];
  dateShowtimes: Showtime[] = [];
  private rawShowtimes: Showtime[] = [];
  private rawDateShowtimes: Showtime[] = [];
  query = '';
  date = '';
  county = '';
  loadingMovie = true;
  loadingShowtimes = true;
  error = '';
  counties = ['Antrim', 'Armagh', 'Cavan', 'Clare', 'Cork', 'Derry', 'Donegal', 'Down', 'Dublin', 'Fermanagh', 'Galway', 'Kerry', 'Kildare', 'Laois', 'Limerick', 'Louth', 'Mayo', 'Meath', 'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary', 'Tyrone', 'Waterford', 'Westmeath', 'Wexford', 'Wicklow'];
  private id = '';
  private queryTimer?: ReturnType<typeof setTimeout>;
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly movies: MovieService,
    private readonly location: LocationFilterService,
  ) {}
  ngOnInit() {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    this.location.state$.subscribe(() => {
      if (this.location.state.mode !== 'all') {
        this.applyFilters();
      } else {
        this.applyLocationFilter();
      }
    });
    this.movies.get(this.id).subscribe({
      next: (movie) => { this.movie = movie; this.loadingMovie = false; },
      error: () => { this.error = 'Could not load this movie.'; this.loadingMovie = false; },
    });
    this.route.queryParamMap.subscribe((params) => {
      this.query = params.get('query') ?? '';
      this.date = params.get('date') ?? '';
      this.county = params.get('county') ?? '';
      this.applyFilters();
    });
  }
  applyFilters() {
    this.loadAvailableDates();
    this.loadShowtimes();
  }
  loadShowtimes() {
    this.loadingShowtimes = true;
    const county = this.location.state.mode !== 'all' ? '' : this.county;
    this.movies.showtimes(this.id, { date: this.date, county }).subscribe({
      next: (rows) => {
        this.rawShowtimes = rows;
        this.applyLocationFilter();
        this.loadingShowtimes = false;
      },
      error: () => { this.error = 'Could not load showtimes for this movie.'; this.loadingShowtimes = false; },
    });
  }
  loadAvailableDates() {
    const county = this.location.state.mode !== 'all' ? '' : this.county;
    this.movies.showtimes(this.id, { county }).subscribe({
      next: (rows) => {
        this.rawDateShowtimes = rows;
        this.applyLocationFilter();
      },
      error: () => (this.dateShowtimes = []),
    });
  }
  selectDate(value: string) {
    this.date = value;
    this.updateQueryParams();
  }
  clearFilters() {
    this.date = '';
    this.county = '';
    this.query = '';
    this.updateQueryParams();
  }
  scheduleQueryUpdate() {
    if (this.queryTimer) clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => this.updateQueryParams(), 350);
  }
  updateQueryParams() {
    if (this.queryTimer) clearTimeout(this.queryTimer);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        query: this.query.trim() || null,
        county: this.county || null,
        date: this.date || null,
        language: null,
        chain: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
  filteredShowtimes(): Showtime[] {
    const term = this.query.trim().toLowerCase();
    if (!term) return this.showtimes;
    return this.showtimes.filter((item) => {
      const haystack = [item.cinema?.name, item.cinema?.city, item.cinema?.county, item.cinema?.chain]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }

  private applyLocationFilter() {
    this.showtimes = this.location.filterCinemas(this.rawShowtimes);
    this.dateShowtimes = this.location.filterCinemas(this.rawDateShowtimes);
  }

  grouped(): CinemaShowtimeGroup[] {
    const map = new Map<string, CinemaShowtimeGroup>();
    this.filteredShowtimes().forEach((item) => {
      const key = item.cinema?.id ?? `${item.cinema?.county}-${item.cinema?.name}`;
      if (!map.has(key)) {
        map.set(key, {
          county: item.cinema?.county ?? 'Unknown',
          cinema: item.cinema?.name ?? 'Unknown cinema',
          chain: item.cinema?.chain ?? 'Cinema',
          city: item.cinema?.city ?? 'Unknown',
          items: [],
        });
      }
      map.get(key)?.items.push(item);
    });
    return Array.from(map.values()).map((group) => ({
      ...group,
      items: group.items.sort((a, b) => a.showTime.localeCompare(b.showTime)),
    }));
  }
  availableDates(): DateChipOption[] {
    const unique = Array.from(new Set(this.dateShowtimes.map((item) => item.showDate.slice(0, 10)))).sort().slice(0, 10);
    return unique.map((value) => {
      const date = new Date(`${value}T00:00:00`);
      return {
        value,
        label: this.relativeDateLabel(value),
        sublabel: date.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' }),
      };
    });
  }
  initials(title: string): string {
    return title.split(/\s+/).slice(0, 2).map((word) => word[0]).join('').toUpperCase();
  }
  private relativeDateLabel(value: string): string {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const isoToday = today.toISOString().slice(0, 10);
    const isoTomorrow = tomorrow.toISOString().slice(0, 10);
    if (value === isoToday) return 'Today';
    if (value === isoTomorrow) return 'Tomorrow';
    return new Date(`${value}T00:00:00`).toLocaleDateString('en-IE', { weekday: 'short' });
  }
}
